import { tokens } from "@core/theme/tokens";
import { useRulesHistories } from "./rulesQueries";
import type { RuleHistoryEntry } from "./rulesApi";

// A6 (human-resolution-spec-212.json): la tarjeta "Historial de cambios de
// reglas" es DEL MÓDULO (admin.jsx:3163-3172 / :3267-3276), al pie de cada
// vista, con la fila de tres columnas when / cambio / user del prototipo.
// El contrato de historial es por regla (`GET .../rules/{id}/history`), así
// que esta tarjeta une en cliente los historiales de las reglas listadas,
// ordenados descendente por fecha — el ajuste del humano que SÍ agrega
// valuesBefore/valuesAfter como línea secundaria, algo que el prototipo no
// modela porque sus semillas no traen esos valores.
const fieldLabels: Record<string, string> = {
  actionId: "Acción",
  points: "Puntos",
  capQuantity: "Tope",
  capFrequency: "Frecuencia del tope",
  validFrom: "Vigente desde",
  validUntil: "Vigente hasta",
  assignment: "Asignación",
  active: "Estado",
};

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (key === "active") return value ? "Activa" : "Inactiva";
  if (key === "assignment") return value === "manual" ? "Manual" : "Automática";
  if (key === "capFrequency") {
    if (value === "month") return "Mes";
    if (value === "year") return "Año";
    return "—";
  }
  return String(value);
}

function diffLines(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined,
): string[] {
  if (!before && !after) return [];
  const keys = new Set([
    ...Object.keys(before ?? {}),
    ...Object.keys(after ?? {}),
  ]);
  const lines: string[] = [];
  for (const key of keys) {
    const beforeValue = before?.[key];
    const afterValue = after?.[key];
    if (JSON.stringify(beforeValue) === JSON.stringify(afterValue)) continue;
    const label = fieldLabels[key] ?? key;
    lines.push(
      `${label}: ${formatValue(key, beforeValue)} → ${formatValue(key, afterValue)}`,
    );
  }
  return lines;
}

function formatWhen(occurredAt: string): string {
  const date = new Date(occurredAt);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = date.toLocaleTimeString("es-EC", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${day} · ${time}`;
}

function HistoryRow({ entry }: { entry: RuleHistoryEntry }) {
  const secondary = diffLines(entry.valuesBefore, entry.valuesAfter);
  return (
    <div
      style={{
        padding: "12px 22px",
        borderTop: `1px solid ${tokens.surface.line}`,
        display: "flex",
        gap: 14,
      }}
    >
      <span
        style={{
          font: `400 11px ${tokens.font.ui}`,
          color: tokens.text.disabled,
          flex: "none",
          width: 128,
        }}
      >
        {formatWhen(entry.occurredAt)}
      </span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            font: `400 12.5px/1.5 ${tokens.font.ui}`,
            color: tokens.text.secondary,
          }}
        >
          {entry.description}
        </div>
        {secondary.length > 0 ? (
          <div
            style={{
              marginTop: 4,
              font: `400 11.5px/1.6 ${tokens.font.ui}`,
              color: tokens.text.tertiary,
            }}
          >
            {secondary.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        ) : null}
      </div>
      <span
        style={{
          font: `500 11.5px ${tokens.font.ui}`,
          color: tokens.text.tertiary,
          flex: "none",
        }}
      >
        {entry.username ?? "—"}
      </span>
    </div>
  );
}

export function RuleHistoryCard({ ruleIds }: { ruleIds: string[] }) {
  const results = useRulesHistories(ruleIds);
  const loading = results.some((result) => result.isLoading);
  const entries = results
    .flatMap((result) => result.data ?? [])
    .sort(
      (a, b) =>
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    );

  return (
    <div
      style={{
        background: tokens.surface.surface,
        border: `1px solid ${tokens.surface.line}`,
        borderRadius: 18,
      }}
    >
      <div style={{ padding: "18px 22px 12px" }}>
        <span
          style={{
            font: `600 11px ${tokens.font.ui}`,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: tokens.text.tertiary,
          }}
        >
          Historial de cambios de reglas
        </span>
      </div>
      {loading ? (
        <div
          style={{
            padding: "12px 22px",
            font: `400 12.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          Cargando…
        </div>
      ) : entries.length === 0 ? (
        <div
          style={{
            padding: "12px 22px",
            font: `400 12.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          Todavía no hay cambios registrados.
        </div>
      ) : (
        entries.map((entry, index) => (
          <HistoryRow key={`${entry.occurredAt}-${index}`} entry={entry} />
        ))
      )}
    </div>
  );
}
